import { Download, Filter, Search, UsersRound } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UFS, api, formatApiError } from "@/lib/api";
import { formatCurrency } from "@/lib/goals";
import { AdminDateField } from "@/components/AdminDateField";

const initialFilters = {
  whatsapp: "all",
  state: "",
  city: "",
  date_from: "",
  date_to: "",
  search: "",
};

const dateLabel = (value) => value ? new Date(value).toLocaleDateString("pt-BR") : "—";

const queryFor = (filters, includeWhatsapp = true) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value && (includeWhatsapp || key !== "whatsapp")) params.set(key, value);
  });
  return params.toString();
};

const WhatsappBadge = ({ authorized, date }) => (
  <div>
    <span className={`rounded-full px-2 py-1 text-xs font-bold ${authorized ? "bg-emerald-100 text-emerald-900" : "bg-slate-100 text-slate-700"}`}>{authorized ? "Autorizou" : "Não autorizou"}</span>
    {authorized && <p className="mt-2 text-xs text-slate-600">{dateLabel(date)}</p>}
  </div>
);

const GoalStats = ({ user }) => (
  <div className="text-sm text-slate-700">
    <p><strong>{user.goal_count}</strong> meta{user.goal_count === 1 ? "" : "s"} · {formatCurrency(user.goal_total_cents)}</p>
    <p className="mt-1">Guardado: <strong>{formatCurrency(user.saved_cents)}</strong></p>
    <p className="mt-1 text-xs">Pix {formatCurrency(user.pix_saved_cents)} · Dinheiro {formatCurrency(user.cash_saved_cents)}</p>
    {user.other_saved_cents > 0 && <p className="mt-1 text-xs">Outros registros {formatCurrency(user.other_saved_cents)}</p>}
  </div>
);

export const AdminUserDirectory = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [users, setUsers] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const loadUsers = useCallback(async () => {
    setError("");
    setUsers(null);
    try {
      const params = new URLSearchParams(queryFor(appliedFilters));
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data.items);
      setTotal(data.total);
    } catch (requestError) {
      setError(formatApiError(requestError));
      setUsers([]);
    }
  }, [appliedFilters, page, pageSize]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const exportAuthorized = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/admin/exports/whatsapp-contacts.csv?${queryFor(appliedFilters, false)}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "contatos-whatsapp-autorizados.csv";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("CSV de contatos autorizados gerado.");
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setExporting(false);
    }
  };

  return (
    <section data-testid="admin-users-directory" className="mt-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Relacionamento</p>
          <h2 className="mt-2 font-heading text-3xl font-extrabold text-emerald-950">Usuários cadastrados</h2>
          <p data-testid="admin-users-total" className="mt-2 text-lg text-slate-600">{total} usuário{total === 1 ? "" : "s"} encontrado{total === 1 ? "" : "s"}</p>
        </div>
        <button type="button" onClick={exportAuthorized} disabled={exporting} data-testid="admin-export-authorized-csv" className="inline-flex min-h-[50px] items-center gap-2 rounded-xl bg-emerald-700 px-4 text-lg font-bold text-white hover:bg-emerald-800 disabled:opacity-60"><Download className="h-5 w-5" />{exporting ? "Gerando..." : "Exportar autorizados CSV"}</button>
      </div>
      <div className="mt-6 grid gap-3 rounded-2xl border border-amber-200 bg-white p-5 md:grid-cols-2 lg:grid-cols-6">
        <label className="lg:col-span-3">
          <span className="mb-2 block text-sm font-bold text-slate-700">
            Busca por nome ou telefone
          </span>
          <span className="relative block">
            <Search className="absolute left-3 top-3 h-5 w-5 text-emerald-700" />
            <input
              data-testid="admin-user-search-input"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
              className="min-h-[48px] w-full rounded-xl border-2 border-slate-300 pl-11 pr-3 text-base focus:border-emerald-600 focus:outline-none"
              placeholder="Ex.: Maria ou (11) 98765-4321"
            />
          </span>
        </label>
        <label className="lg:col-span-3">
          <span className="mb-2 block text-sm font-bold text-slate-700">WhatsApp</span>
          <select
            data-testid="admin-whatsapp-filter"
            value={filters.whatsapp}
            onChange={(event) => updateFilter("whatsapp", event.target.value)}
            className="min-h-[48px] w-full rounded-xl border-2 border-slate-300 bg-white px-3 text-base focus:border-emerald-600 focus:outline-none"
          >
            <option value="all">Todos os usuários</option>
            <option value="authorized">Autorizaram WhatsApp</option>
            <option value="not_authorized">Não autorizaram</option>
          </select>
        </label>
        <label className="lg:col-span-2">
          <span className="mb-2 block text-sm font-bold text-slate-700">Estado</span>
          <select
            data-testid="admin-state-filter"
            value={filters.state}
            onChange={(event) => updateFilter("state", event.target.value)}
            className="min-h-[48px] w-full rounded-xl border-2 border-slate-300 bg-white px-3 text-base focus:border-emerald-600 focus:outline-none"
          >
            <option value="">Todos</option>
            {UFS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        </label>
        <label className="lg:col-span-2">
          <span className="mb-2 block text-sm font-bold text-slate-700">Cidade</span>
          <input
            data-testid="admin-city-filter"
            value={filters.city}
            onChange={(event) => updateFilter("city", event.target.value)}
            className="min-h-[48px] w-full rounded-xl border-2 border-slate-300 px-3 text-base focus:border-emerald-600 focus:outline-none"
            placeholder="Ex.: São Paulo"
          />
        </label>
        <div className="lg:col-span-1">
          <AdminDateField
            label="Cadastro de"
            testId="admin-date-from-filter"
            value={filters.date_from}
            onChange={(value) => updateFilter("date_from", value)}
          />
        </div>
        <div className="lg:col-span-1">
          <AdminDateField
            label="Cadastro até"
            testId="admin-date-to-filter"
            value={filters.date_to}
            onChange={(value) => updateFilter("date_to", value)}
          />
        </div>
        <button
          type="button"
          onClick={applyFilters}
          data-testid="admin-apply-filters-button"
          className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border-2 border-emerald-200 bg-emerald-50 px-4 text-lg font-bold text-emerald-950 transition-colors hover:bg-emerald-100 lg:col-span-6"
        >
          <Filter className="h-5 w-5" />
          Aplicar filtros
        </button>
      </div>
      {error && <p data-testid="admin-users-error" role="alert" className="mt-5 rounded-xl border-2 border-red-200 bg-red-50 p-4 text-red-800">{error}</p>}
      {!error && users === null && <div data-testid="admin-users-loading" className="mt-6 h-52 animate-pulse rounded-2xl bg-white/70" />}
      {!error && users?.length === 0 && <div data-testid="admin-users-empty" className="mt-6 rounded-2xl border-2 border-dashed border-amber-300 bg-white/70 p-10 text-center"><UsersRound className="mx-auto h-9 w-9 text-amber-700" /><p className="mt-3 text-lg font-bold text-emerald-950">Nenhum usuário encontrado.</p></div>}
      {!error && users?.length > 0 && <><div className="mt-6 hidden overflow-x-auto rounded-2xl border border-amber-200 bg-white md:block"><table data-testid="admin-users-table" className="w-full min-w-[980px] text-left"><thead className="bg-amber-50 text-sm text-emerald-950"><tr><th className="p-4">Usuário</th><th className="p-4">Local</th><th className="p-4">Cadastro</th><th className="p-4">WhatsApp</th><th className="p-4">Metas e progresso</th></tr></thead><tbody>{users.map((user) => <tr key={user.id} data-testid={`admin-user-row-${user.id}`} className="border-t border-amber-100 align-top"><td className="p-4"><p className="font-bold text-emerald-950">{user.name}</p><p className="mt-1 text-sm text-slate-700">{user.phone}</p>{user.email && <p className="mt-1 break-all text-sm text-slate-600">{user.email}</p>}</td><td className="p-4 text-sm text-slate-700">{user.city} · {user.state}</td><td className="p-4 text-sm text-slate-700">{dateLabel(user.created_at)}</td><td className="p-4"><WhatsappBadge authorized={user.whatsapp_authorized} date={user.whatsapp_authorized_at} /></td><td className="p-4"><GoalStats user={user} /></td></tr>)}</tbody></table></div><ul data-testid="admin-users-mobile-list" className="mt-6 grid gap-4 md:hidden">{users.map((user) => <li key={user.id} className="rounded-2xl border border-amber-200 bg-white p-5"><p className="font-heading text-xl font-extrabold text-emerald-950">{user.name}</p><p className="mt-1 text-base text-slate-700">{user.phone}</p>{user.email && <p className="mt-1 break-all text-sm text-slate-600">{user.email}</p>}<p className="mt-3 text-sm text-slate-700">{user.city} · {user.state} · Cadastro {dateLabel(user.created_at)}</p><div className="mt-3"><WhatsappBadge authorized={user.whatsapp_authorized} date={user.whatsapp_authorized_at} /></div><div className="mt-4 border-t border-amber-100 pt-4"><GoalStats user={user} /></div></li>)}</ul></>}
      {!error && users?.length > 0 && Math.ceil(total / pageSize) > 1 && <div data-testid="admin-users-pagination" className="mt-6 flex items-center justify-between gap-3"><button type="button" onClick={() => setPage((current) => Math.max(current - 1, 1))} disabled={page === 1} data-testid="admin-users-previous-page" className="min-h-[44px] rounded-xl border-2 border-emerald-200 bg-white px-4 font-bold text-emerald-900 hover:bg-emerald-50 disabled:opacity-50">Anterior</button><p className="font-bold text-slate-700">Página {page} de {Math.ceil(total / pageSize)}</p><button type="button" onClick={() => setPage((current) => Math.min(current + 1, Math.ceil(total / pageSize)))} disabled={page >= Math.ceil(total / pageSize)} data-testid="admin-users-next-page" className="min-h-[44px] rounded-xl border-2 border-emerald-200 bg-white px-4 font-bold text-emerald-900 hover:bg-emerald-50 disabled:opacity-50">Próxima</button></div>}
    </section>
  );
};